import {
  getName,
  getTeams,
  isDefined,
  isEmpty,
  type TinyUser,
  type PermissionType,
  type UserData,
  type UserProfileData,
} from '@terros/common'
import { RANDOM_COLORS } from '../color'

export function matchUser(
  user: Pick<UserData, 'firstName' | 'lastName' | 'preferredName' | 'email' | 'phone' | 'teams'>,
  searchText?: string
): boolean {
  if (isEmpty(searchText)) return true
  const needle = searchText.toUpperCase()
  const { email, phone, teams } = user
  if (getName(user).toUpperCase().includes(needle)) return true
  if (
    getName({ ...user, preferredName: undefined })
      .toUpperCase()
      .includes(needle)
  )
    return true
  if (email.toUpperCase().includes(needle)) return true
  if (phone?.toUpperCase().includes(needle)) return true
  const teamNames = getTeams(teams).map((t) => t.name.toUpperCase())
  if (teamNames.some((name) => name.includes(needle))) return true
  return false
}

export function sortUsersByLastName(
  a: Pick<UserData, 'firstName' | 'lastName' | 'preferredName'>,
  b: Pick<UserData, 'firstName' | 'lastName' | 'preferredName'>
): number {
  if (!a) return -1
  if (!b) return 1
  if (a.lastName !== b.lastName) {
    return a.lastName.trim().localeCompare(b.lastName.trim())
  }
  const aFirst = getFirstName(a)
  const bFirst = getFirstName(b)
  return aFirst.localeCompare(bFirst)
}

export function sortUsersByFirstName(
  a: Pick<UserData, 'firstName' | 'lastName' | 'preferredName'>,
  b: Pick<UserData, 'firstName' | 'lastName' | 'preferredName'>
): number {
  if (!a) return -1
  if (!b) return 1

  const aFirst = getFirstName(a)
  const bFirst = getFirstName(b)
  if (aFirst !== bFirst) {
    return aFirst.trim().localeCompare(bFirst.trim())
  }
  return a.lastName.trim().localeCompare(b.lastName.trim())
}

export function getFirstName({ preferredName, firstName }: Pick<UserData, 'firstName' | 'preferredName'>): string {
  return preferredName?.trim() || firstName.trim()
}

/** checks if user has permission beyond self scope (downline, company, withinRestrictions) */
export function hasUserPermissionBeyondSelf(
  user: Pick<UserProfileData, 'permissionMap'> | undefined,
  permissionType: PermissionType
): boolean {
  if (!user) return false
  const scopes = user.permissionMap?.[permissionType]
  if (isEmpty(scopes)) return false
  return scopes.some((s) => s.entity !== 'none' && s.entity !== 'self')
}

const getColorFromId = (id: string): string => {
  let hash = 0

  for (const character of id) {
    hash = (hash << 5) - hash + character.charCodeAt(0)
    hash |= 0
  }
  const idx = Math.abs(hash) % RANDOM_COLORS.length
  return RANDOM_COLORS[idx]
}

export const getUserColor = (user: Pick<TinyUser, 'userId' | 'color'>): string => {
  if (isDefined(user.color)) return user.color
  return getColorFromId(user.userId)
}
